import { WarningOutlined } from '@ant-design/icons';
import { Alert, Layout } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  BrowserRouter as Router, Switch,
} from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import './App.css';
import ProtectedRoute from './Routers/ProtectedRoute';
import './Styles/CommonStyles.css';
import allActions from './actions';
import { showNotification } from './actions/utilities/Notifications/NotificationActions';
import AppBar from './components/Menu/appBar/AppBar';
import MobileHeader from './components/Menu/appBar/mobileHeader';
import MainMenu from './components/Menu/sideMenu/Menu';
import ConfirmModal from './components/confirmModal/ConfirmModal';
import ErrorComponent from './components/errorComponent/ErrorComponent';
import ModalContainer from './components/modalContainer';
import NotificationMsg from './components/notifications/NotificationMsg';
import { ThemeProvider } from './contexts/ThemeContext';
import PlannedOrdersContext from './contexts/plannedOrdersContext';
import SelectedItemsContext from './contexts/selectedItemsContext';
import usePlannedOrders from './hooks/contextHooks/usePlannedOrders';
import useSelectedItems from './hooks/contextHooks/useSelectedItems';
import { appInsights } from './middleware/appInsights';
import {
  Config,
  DynamicWorklists, MassUpdate, MaterialListView, MaterialMasterView, MonetaryImpactSummary, StaticWorklists
} from './pages';
import ConfigViewContainer from './pages/Config/configurationSettings/ConfigViewContainer';
import Dashboard from './pages/Dashboard';
import FeatureTimeline from './pages/FeatureTimeline';
import MaterialDetails from './pages/MaterialDetails';
import MMStaticWLContainer from './pages/MaterialMaster/worklistContainers/MMStaticWLContainer';
import MMWorklistContainer from './pages/MaterialMaster/worklistContainers/MMWorklistContainer';
import NewStockAddition from './pages/NewStockAddition/NewStockAddition';
import Settings from './pages/Settings';
import AllSimResultsContainer from './pages/SimulationViews/AllSimResultsContainer';
import Scenario from './pages/SimulationViews/allSimResults/scenario/Scenario';
import StaticWLContainer from './pages/Worklists/StaticWLContainer';
import WorklistContainer from './pages/Worklists/WorklistContainer';
import downloadFile from './utils/DownloadFunctions';
import { listModes } from './utils/GlobalConstants';

const { Sider } = Layout;

const App = ({ msalInstance }) => {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.authState);
  const settingsLoaded = useSelector((state) => state.commonState.loaded?.userSettings);
  const userSettings = useSelector((state) => state.commonState.userSettings);
  const jobID = useSelector((state) => state.commonState.CSVID);
  const csv = useSelector((state) => state.commonState.CSV);
  const warningText = useSelector((state) => state.commonState.warningText);
  const outdated = useSelector((state) => state.commonState.outdated);
  const plants = useSelector((state) => state.commonState.plants);
  const history = useHistory();

  const [showPopup, setShowPopup] = useState(false);

  // to limit access, uncomment the code below and import userHasAuthenticatedRole
  // const [hasAccess, setHasAccess] = useState(false);
  // useEffect(() => {
  //   if (authState?.user?.idTokenClaims?.roles) {
  //     const userHasAuthenticatedRole = hasAuthenticatedRole(
  //       authState?.user.idTokenClaims.roles,
  //       process.env.REACT_APP_CONFIG_ACCESS,
  //     );
  //     setHasAccess(userHasAuthenticatedRole);
  //   }
  // }, [authState?.user?.idTokenClaims, authState?.user?.idTokenClaims?.roles]);

  const hasAccess = true;

  useEffect(() => {
    dispatch(allActions.CommonActions.getPlants());
  }, []);

  useEffect(() => {
    // load resources into redux on start, but wait for
    // plants so the token is received
    if (plants) {
      dispatch(allActions.CommonActions.getCompanyCodes());
      dispatch(allActions.CommonActions.getColumnMapper());
      dispatch(allActions.MaterialListActions.fetchMaterialColumns());
      dispatch(allActions.MaterialListActions.getDropdownValues());
      dispatch(allActions.MMActions.fetchMaterialColumns());
      dispatch(allActions.CommonActions.getPlantGroups());
      dispatch(allActions.CommonActions.getConfigTable());
      dispatch(allActions.CommonActions.getWarnings());
      dispatch(allActions.CommonActions.getOutdated());
    }
  }, [plants]);

  useEffect(() => {
    if (!outdated || outdated === 'false') {
      setShowPopup(false);
    } else if (outdated === 'true') {
      const hasSeenPopup = localStorage.getItem('seenPopUp');
      setShowPopup(!hasSeenPopup);
    }
  }, [outdated]);

  const hidePopup = () => {
    localStorage.setItem('seenPopUp', true);
    setShowPopup(false);
  };

  useEffect(() => {
    let intervalID;
    if (jobID) {
      if (csv?.error) {
        dispatch(showNotification(
          {
            Type: 'error',
            Title: 'CSV Retrieval Failure',
            Message: 'Failed to generate the exported file',
          },
        ));
        clearInterval(intervalID);
        dispatch(allActions.CommonActions.clearCSV());
        return undefined;
      }
      if (csv?.done) {
        downloadFile(csv.url);
        clearInterval(intervalID);
        dispatch(allActions.CommonActions.clearCSV());
      } else if (!csv?.done) {
        intervalID = setInterval(() => {
          dispatch(allActions.CommonActions.getCSV(jobID));
        }, 2000);
      }
    }
    return () => {
      clearInterval(intervalID);
    };
  }, [jobID, csv]);

  useEffect(() => {
    if (!authState?.user?.account) {
      const account = msalInstance.getActiveAccount();
      if (!account) return;
      dispatch(allActions.AuthActions.updateAccountData(account));
    }
  }, [authState?.user]);

  useEffect(() => {
    if (authState.user?.uniqueId) {
      dispatch(allActions.CommonActions.setLoading('USER_SETTINGS'));
      dispatch(
        allActions.CommonActions.getAllUserSettings(authState.user.uniqueId),
      );
      dispatch(allActions.CommonActions.getWidgetViews());
      dispatch(allActions.CommonActions.getCurrencyList());
      dispatch(allActions.WorkListActions.getStaticWLs(authState.user.uniqueId, listModes.matList));
      dispatch(allActions.WorkListActions.getStaticWLs(authState.user.uniqueId, listModes.mmList));
      appInsights?.setAuthenticatedUserContext(authState.user.uniqueId);
      appInsights?.trackPageView();
      dispatch(allActions.CommonActions.trackUniqueIds(authState.user.uniqueId));
      dispatch(allActions.CommonActions.getESStatus());
      const intervalES = setInterval(() => {
        dispatch(allActions.CommonActions.getESStatus());
      }, 60000);
      return () => clearInterval(intervalES);
    }
  }, [authState?.user?.uniqueId]);

  useEffect(() => {
    if (settingsLoaded) {
      dispatch(allActions.CommonActions.setUseDefaultSettings(
        !!userSettings?.USE_DEFAULTS_GLOBALLY,
      ));
    }
  }, [settingsLoaded]);

  const { origin } = window;
  const isProd = !origin.includes('localhost') && !origin.includes('dev') && !origin.includes('acc');

  const theme = localStorage.getItem('theme');

  useEffect(() => {
    // Dynamically import the appropriate stylesheet
    if (theme === 'dark') {
      import('./Styles/darkStyles.css');
    } else {
      import('./Styles/lightStyles.css');
    }
  }, [theme]);

  return (
    <ThemeProvider>
      <Router>
        {
          hasAccess ? (
            <SelectedItemsContext.Provider value={useSelectedItems()}>
              <Layout>
                {
                warningText && (
                  <Alert
                    message={<div style={{ fontSize: '22px' }}>{warningText}</div>}
                    type="warning"
                    icon={<WarningOutlined style={{ fontSize: '28px' }} />}
                    showIcon
                    banner
                    closable
                  />
                )
              }

                <ConfirmModal
                  title="Outdated data"
                  visible={showPopup}
                  message="IOTA data is from Saturday 2023-04-22, due to SAP maintenance Sunday 2023-04-23"
                  hideFooter
                  onCancel={hidePopup}
                />
                <PlannedOrdersContext.Provider value={usePlannedOrders()}>
                  <div className="mainLayout">
                    <MainMenu isProd={isProd} />
                    <div className="innerLayout">
                      <div className="mobileHeader">
                        <MobileHeader isProd={isProd} />
                      </div>
                      <div className="desktopHeader">
                        <AppBar isProd={isProd} />
                      </div>
                      <div className="content">
                        <Switch>
                          <Route path="/" exact>
                            <Redirect to="/dashboard" />
                          </Route>
                          <Route path="/dashboard" component={Dashboard} />
                          <Route path="/simulations/:runID?" component={AllSimResultsContainer} />
                          <Route path="/simulation/new-stock" component={NewStockAddition} />
                          <Route path="/simulation/scenario" component={Scenario} />
                          <Route path="/material-details/:plant/:material" component={MaterialDetails} />
                          <Route path="/feature-timeline" component={FeatureTimeline} />
                          <Route path="/settings" component={Settings} />
                          <Route path="/material-list" component={MaterialListView} />
                          <Route path="/material-master" component={MaterialMasterView} exact />
                          <Route path="/work-lists" component={DynamicWorklists} />
                          <Route path="/work-list/:worklistID?" component={WorklistContainer} />
                          <Route path="/material-master/work-list/:worklistID?" component={MMWorklistContainer} />
                          <Route path="/mass-update" component={MassUpdate} exact />
                          <Route path="/static-work-lists" component={StaticWorklists} />
                          <Route path="/static-work-list/:worklistID?" component={StaticWLContainer} />
                          <Route path="/material-master/static-work-list/:worklistID?" component={MMStaticWLContainer} />
                          <Route path="/monetary-impact" component={MonetaryImpactSummary} />
                          <Route path="/config-view" component={ConfigViewContainer} />
                          <ProtectedRoute path="/config" requiredRoles={['SuperUser']} redirectPath="/dashboard">
                            <Config />
                          </ProtectedRoute>
                        </Switch>
                        <NotificationMsg />
                      </div>
                      <ModalContainer />
                    </div>
                  </div>
                </PlannedOrdersContext.Provider>
              </Layout>
            </SelectedItemsContext.Provider>
          ) : (
            <Switch>
              <Route path="/error" component={ErrorComponent} />
              <Redirect from="*" to="/error" />
            </Switch>
          )
        }
      </Router>
    </ThemeProvider>
  );
};
export default App;

App.propTypes = {
  msalInstance: PropTypes.objectOf(PropTypes.any).isRequired,
};
